import { takeLatest, call, put, all } from "redux-saga/effects";
import Cookies from "js-cookie";
import moment from "moment";

import KYCActionTypes from "./types";
import {
    setKYCSuccess,
    setKYCFailure,
    fetchKYCSuccess,
    fetchKYCFailure,
    fetchReKycSuccess,
    fetchReKycFailure,
    fetchKycFromProviderSuccess,
    fetchKycFromProviderFailure,
    setReKycSuccess,
    setReKycFailure,
    setReKycStart,
    fetchKYCSuccessFromAdmin,
    fetchKYCFailureFromAdmin,
    fetchKycServerError,
    fetchKYCAllDetailsStart,
    fetchKycStatusSuccess,
    fetchKycStatusFailure,
    updateKYCFormData,
} from "./actions";

import {
    getKYC,
    getKYCCombined,
    getUserKYCDetailsFromAdmin,
    getReKYCFlag,
    setReKYCFlag,
    submitKYC,
    getLionwareByTenant,
    getUserExists,
    getKYCExists,
} from "api/FnxtApis";
import { extractKYCdata } from "store/helpers";
import { pick } from "lodash";
import { electronicFormatIBAN } from "ibantools";
import { fetchUserDetailsStart } from "../account/actions";
import { SPARKASSE_REGISTRIERUNGSDATEN } from "../../../../common/constants";
import { familyStatusesMapping } from "constants/index";

function mapGermanMaritalStatusToEnglish(germanValue) {
    const germanToEnglishMapping = {
        ledig: "single",
        zusammenlebend: "living-together",
        "verheiratet / eingetr. Lebenspartnerschaft": "married",
        verheiratet: "married",
        "eingetr. Lebenspartnerschaft": "married",
        Lebenspartnerschaft: "partnership",
        "getrennt lebend": "separated-alive",
        geschieden: "divorced",
        verwitwet: "widowed",
        "Lebenspartner verstorben": "partner-deceased",
        "im Scheidungspozess": "in-divorce",
    };
    const trimmedValue = germanValue?.trim();
    const englishValue = germanToEnglishMapping[trimmedValue];
    const foundMapping = familyStatusesMapping.find((item) => englishValue === item.value);
    return foundMapping;
}

function extractKYCAPIdata(kycFormData, isProfileSubmission) {
    const userFields = ["Email", "TypeGender", "TypeTitle", "FirstName", "LastName"];

    //additional field added if changed from profile section
    !isProfileSubmission && userFields.push("Birthday", "IsUSTaxation", "IsPEP", "PreferredLang");
    let user = pick(kycFormData, userFields);
    user.TypeTitle = user?.TypeTitle?.value;

    const Address = pick(kycFormData, [
        "TypeAddress",
        "Street",
        "Streetnumber",
        "StreetAddition",
        "Zipcode",
        "City",
        "Region",
        "Country",
    ]);

    Address["TypeAddress"] = Address?.TypeAddress || "typeAddressPrimary";
    Address["Country"] = Address?.Country?.value;
    !Address.StreetAddition && delete Address.StreetAddition;
    !Address.Region && delete Address.Region;
    user = {
        ...user,
        Address: [Address],
    };

    //
    if (!isProfileSubmission) {
        user.IsUSTaxation = user?.IsUSTaxation === "true";
        user.IsPEP = user?.IsPEP === "true";
        const BankAccount = pick(kycFormData, ["IBAN", "BIC", "Owner", "TypeBankAccount"]) || {};
        BankAccount.IBAN = electronicFormatIBAN(BankAccount.IBAN);
        BankAccount["TypeBankAccount"] = BankAccount.TypeBankAccount || "typeBankAccountUserPrimary";

        const Phone = pick(kycFormData, ["CountryCode", "DialCode", "Number", "TypePhone"]);
        Phone["TypePhone"] = Phone.TypePhone || "typePhoneMobile";
        Phone["CountryCode"] = Phone?.CountryCode?.toUpperCase() || "";

        const Identification = pick(kycFormData, "Birthplace", "BirthCountry", "Nationality", "MaritalStatus");
        Identification["BirthCountry"] = Identification?.BirthCountry?.value;
        Identification["Nationality"] = Identification?.Nationality?.value;
        Identification["MaritalStatus"] = Identification?.MaritalStatus?.value;

        user = {
            ...user,
            BankAccount: [BankAccount],
            Phone: [Phone],
            Identification: Identification,
        };
    } else {
        user.TypeGender = user?.TypeGender?.value;
    }

    return { user };
}

//Update KYC
function* setReKYCAsync() {
    try {
        const response = yield call(setReKYCFlag, { ReKYC: true });
        if (response.status == 200 || response.status == 201) {
            yield put(
                setReKycSuccess({
                    reKYC: response?.data?.ReKYC,
                    isSubmittingReKycSuccess: true,
                }),
            );
        } else {
            throw new TypeError(response.originalError.message);
        }
    } catch (err) {
        yield put(setReKycFailure(err));
    }
}

//Update KYC
function* setKYCAsync({ payload }) {
    try {
        const kycPayload = extractKYCAPIdata(payload.user, payload.fromProfile);
        const response = yield call(submitKYC, kycPayload, payload.currentKYCStatus);
        if ((response.status == 200 || response.status == 201) && response.data) {
            //Start Re-KYC if user patched from Profile
            if (payload.enableReKYC) {
                yield put(setReKycStart());
            }
            const kyc = response.data;
            const kycFormData = extractKYCdata(kyc);
            yield put(
                setKYCSuccess({
                    kycData: kyc,
                    kycFormData: kycFormData,
                    hasUserInitiated: true,
                    isSubmittingKycSuccess: true,
                }),
            );
            yield put(fetchUserDetailsStart());
        } else {
            yield put(setKYCFailure(response?.messsage || response?.originalError?.message));
        }
    } catch (err) {
        yield put(setKYCFailure(err));
    }
}

function* fetchKYCAsync() {
    try {
        // check if user data exists
        const userRes = yield call(getUserExists);
        const status = userRes?.status;
        const userExists = userRes?.data?.exists;

        if (status === 404 || (status === 200 && !userExists) || status === null || (status >= 500 && status < 600)) {
            return;
        }

        const response = yield call(getKYC);

        const kyc = response?.data;
        if (response.status === 200 && kyc) {
            const kycFormData = extractKYCdata(kyc);

            yield put(
                fetchKYCSuccess({
                    kycData: kyc,
                    kycFormData: kycFormData,
                    hasUserInitiated: true,
                }),
            );
        } else if (response.status === 404) {
            yield put(fetchKYCFailure(kyc.detail));
        } else if ((response.status >= 500 && response.status < 600) || response.status === null) {
            yield put(fetchKycServerError(true));
        } else {
            throw new TypeError(response.originalError.message);
        }
    } catch (err) {
        yield put(fetchKYCFailure(err));
    }
}

function* fetchKYCDataAsyncForAdmin({ payload }) {
    const userId = payload;

    try {
        const response = yield call(getUserKYCDetailsFromAdmin, userId);

        const kyc = response?.data;
        if (response.status === 200 && kyc) {
            const kycFormData = kyc?.User ? extractKYCdata(kyc?.User) : {};
            const kycedData = kyc?.KYC?.List?.[0];
            const reKYC = kyc?.ReKYC?.ReKYC;

            yield put(
                fetchKYCSuccessFromAdmin({
                    kycData: kyc?.User || {},
                    kycFormData: kycFormData,
                    hasUserInitiated: !!kyc?.User,
                    isUserKYCed: kycedData && !reKYC,
                    providerData: kycedData,
                    reKYC: reKYC,
                }),
            );
        } else if (response.status == 404) {
            yield put(fetchKYCFailureFromAdmin(kyc.detail));
        } else {
            throw new TypeError(response.originalError.message);
        }
    } catch (err) {
        yield put(fetchKYCFailureFromAdmin(err));
    }
}

function* fetchInheritUserInitialKYCDataAsyncForAdmin({ payload }) {
    const userId = payload;

    try {
        const response = yield call(getUserKYCDetailsFromAdmin, userId);

        const kyc = response?.data;
        if (response.status == 200 && kyc) {
            const kycFormData = kyc?.User ? extractKYCdata(kyc?.User) : {};
            const kycedData = kyc?.KYC?.List?.[0];
            const reKYC = kyc?.ReKYC?.ReKYC;

            yield put(
                fetchKYCSuccess({
                    kycData: kyc?.User,
                    kycFormData: kycFormData,
                    hasUserInitiated: true,
                }),
            );
            if (kycedData) {
                yield put(fetchKycFromProviderSuccess({ isUserKYCed: true, providerData: kycedData }));
            }
            yield put(
                fetchReKycSuccess({
                    reKYC: reKYC,
                }),
            );
        } else if (response.status == 404) {
            yield put(fetchKYCFailure(kyc.detail));
            yield put(fetchKycFromProviderFailure(kyc.detail));
            yield put(fetchReKycFailure(kyc.detail));
        } else if ((response.status >= 500 && response.status < 600) || response.status == null) {
            yield put(fetchKycServerError(true));
        } else {
            throw new TypeError(response.originalError.message);
        }
    } catch (err) {
        yield put(fetchKYCFailure(err));
        yield put(fetchKycFromProviderFailure(err));
        yield put(fetchReKycFailure(err));
    }
}

function* fetchAllKYCAsync({ payload }) {
    try {
        // check if user data exists
        const userRes = yield call(getUserExists);
        const status = userRes?.status;
        const userExists = userRes?.data?.exists;

        if (status === 404 || (status === 200 && !userExists) || status === null) {
            const sparkasseRegistrierungsdaten = Cookies.get(SPARKASSE_REGISTRIERUNGSDATEN);
            if (sparkasseRegistrierungsdaten) {
                try {
                    const registrierungsdaten = JSON.parse(sparkasseRegistrierungsdaten);
                    const hasMultipleAccounts = registrierungsdaten.konten && registrierungsdaten.konten.length > 1;

                    const kycFormData = {
                        personal: {
                            FirstName: registrierungsdaten.personendaten.vorname,
                            LastName: registrierungsdaten.personendaten.nachname,
                            Street: registrierungsdaten.wohnsitz.strasse,
                            Streetnumber: registrierungsdaten.wohnsitz.hausnummer,
                            StreetAddition: registrierungsdaten.wohnsitz.adresszusatz,
                            City: registrierungsdaten.wohnsitz.stadt,
                            Zipcode: registrierungsdaten.wohnsitz.postleitzahl,
                            IBAN: registrierungsdaten.konten[0].iban,
                            BIC: registrierungsdaten.konten[0].bic,
                            MaritalStatus: mapGermanMaritalStatusToEnglish(registrierungsdaten.personendaten.familienstand),
                            ...(hasMultipleAccounts && {
                                availableBankAccounts: registrierungsdaten.konten,
                                selectedBankAccountIndex: 0,
                                hasMultipleBankAccounts: true,
                            }),
                        },
                        identification: {
                            Birthday: moment(registrierungsdaten.personendaten.geburtsdatum, "DD.MM.YYYY").format("YYYY-MM-DD"),
                            Birthplace: registrierungsdaten.personendaten.geburtsort,
                        },
                    };
                    yield put(updateKYCFormData?.(kycFormData));
                } catch (error) {
                    Cookies.remove(SPARKASSE_REGISTRIERUNGSDATEN);
                    console.error("Error parsing Sparkasse registrierungsdaten:", error);
                }
                Cookies.remove(SPARKASSE_REGISTRIERUNGSDATEN);
            }
            return;
        } else if (status >= 500 && status < 600) {
            return;
        }

        // Step 1: Call fetchKYCAsync
        const kycResponse = yield call(getKYC);
        const kyc = kycResponse?.data;

        if (kycResponse.status === 200 && kyc) {
            const kycFormData = extractKYCdata(kyc);
            yield put(
                fetchKYCSuccess({
                    kycData: kyc,
                    kycFormData: kycFormData,
                    hasUserInitiated: true,
                }),
            );

            // check if provider KYC data exists
            const userKYCRes = yield call(getKYCExists);
            const kycResStatus = userKYCRes?.status;
            const kycExists = userKYCRes?.data?.exists;
            if (
                kycResStatus === 404 ||
                (kycResStatus === 200 && !kycExists) ||
                kycResStatus === null ||
                (kycResStatus >= 500 && kycResStatus < 600)
            ) {
                return;
            }

            // Step 2: Call fetchKycFromProviderAsync
            const providerResponse = yield call(getKYCCombined);

            const providerKYC = providerResponse?.data?.List;

            if (providerResponse.status === 200 && providerKYC) {
                yield put(fetchKycFromProviderSuccess({ isUserKYCed: true, providerData: providerKYC?.[0] }));

                // Step 3: Call fetchReKycAsync
                const reKycResponse = yield call(getReKYCFlag);
                if (reKycResponse.status === 200) {
                    const reKycStatus = reKycResponse?.data?.ReKYC;
                    yield put(fetchReKycSuccess({ reKYC: reKycStatus }));

                    // Step 4: Call KYC Status
                    if (reKycStatus === true) {
                        const kycStatusResponse = yield call(getLionwareByTenant, payload?.tenant);
                        if (kycStatusResponse.status === 200) {
                            const kycStatus = kycStatusResponse?.data?.Status;
                            yield put(fetchKycStatusSuccess({ statusKyc: kycStatus }));
                        } else if (
                            (kycStatusResponse.status >= 500 && kycStatusResponse.status < 600) ||
                            kycStatusResponse.status === null
                        ) {
                            yield put(fetchKycStatusFailure(true));
                        } else {
                            throw new TypeError(kycStatusResponse.originalError.message);
                        }
                    }
                } else if ((reKycResponse.status >= 500 && reKycResponse.status < 600) || reKycResponse.status === null) {
                    yield put(fetchKycServerError(true));
                } else {
                    throw new TypeError(reKycResponse.originalError.message);
                }
            } else if ((providerResponse.status >= 500 && providerResponse.status < 600) || providerResponse.status === null) {
                yield put(fetchKycServerError(true));
            } else {
                throw new TypeError(providerResponse.originalError.message);
            }
        } else if ((kycResponse.status >= 500 && kycResponse.status < 600) || kycResponse.status === null) {
            yield put(fetchKycServerError(true));
        } else {
            throw new TypeError(kycResponse.originalError.message);
        }
    } catch (err) {
        yield put(fetchKYCFailure(err));
    }
}

function* fetchKYCAllDetailsStartFn() {
    yield takeLatest(KYCActionTypes.FETCH_KYC_ALL_DETAILS_START, fetchAllKYCAsync);
}
function* callTheInitialKYCMethods({ payload }) {
    yield put(fetchKYCAllDetailsStart(payload));
}

function* setKYCStartFn() {
    yield takeLatest(KYCActionTypes.SET_KYC_START, setKYCAsync);
}

function* setReKYCStartFn() {
    yield takeLatest(KYCActionTypes.SET_REKYC_START, setReKYCAsync);
}

function* fetchKYCStartFn() {
    yield takeLatest(KYCActionTypes.FETCH_KYC_START, fetchKYCAsync);
}

//ADMIN
function* fetchKYCDataStartAdminFn() {
    yield takeLatest(KYCActionTypes.FETCH_KYC_START_ADMIN, fetchKYCDataAsyncForAdmin);
}

function* callTheInitialKYCFn() {
    yield takeLatest(KYCActionTypes.CALL_INITIAL_KYC_METHODS, callTheInitialKYCMethods);
}

function* callTheInheritUserInitialKYCFn() {
    yield takeLatest(KYCActionTypes.CALL_INHERIT_USER_INITIAL_KYC_METHOD, fetchInheritUserInitialKYCDataAsyncForAdmin);
}

export function* kycSagas() {
    yield all([
        call(setKYCStartFn),
        call(setReKYCStartFn),
        call(fetchKYCStartFn),
        call(fetchKYCAllDetailsStartFn),
        //ADMIN
        call(fetchKYCDataStartAdminFn),
        call(callTheInitialKYCFn),
        call(callTheInheritUserInitialKYCFn),
    ]);
}
